export function isApiSuccess(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const record = body as Record<string, unknown>;
  if (record.success === true || record.success === 'true') return true;
  if (record.status === true) return true;
  if (String(record.status ?? '').toUpperCase() === 'SUCCESS') return true;
  if (record.code === 200 || record.statusCode === 200) return true;
  return false;
}

export function extractApiData<T>(body: unknown): T | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  if (record.data !== undefined) return record.data as T;
  return body as T;
}

/**
 * Format currency values
 */
export function normalizeCurrencyCode(currency: unknown): string {
  return String(currency ?? '').trim().toUpperCase();
}

export function getCurrencySymbol(currency: string): string {
  const code = normalizeCurrencyCode(currency);
  if (!code) return '';

  try {
    const symbol = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value;

    if (symbol && symbol.toUpperCase() !== code) return symbol;
  } catch {
    // invalid ISO currency code
  }

  return code;
}

type FormatCurrencyAmountOptions = {
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  includeCode?: boolean;
};

export function formatCurrencyAmount(
  amount: number,
  currency: string,
  options: FormatCurrencyAmountOptions = {},
): string {
  const code = normalizeCurrencyCode(currency);
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return '—';

  const locale = options.locale ?? 'en';
  const numberOptions = {
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits,
  };

  if (!code) {
    return numericAmount.toLocaleString(locale, numberOptions);
  }

  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
      ...numberOptions,
    }).format(numericAmount);

    return options.includeCode ? `${formatted} ${code}` : formatted;
  } catch {
    const amountText = numericAmount.toLocaleString(locale, numberOptions);
    const symbol = getCurrencySymbol(code);
    const formatted = symbol ? `${symbol}${amountText}` : `${amountText} ${code}`;
    return options.includeCode ? `${formatted} ${code}` : formatted;
  }
}

type FormatCurrencyOptions = Omit<
  FormatCurrencyAmountOptions,
  'minimumFractionDigits' | 'maximumFractionDigits'
>;

/** Standard 2-decimal currency display — use this instead of repeating fraction options. */
export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  options: FormatCurrencyOptions = {},
): string {
  return formatCurrencyAmount(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  });
}

/**
 * Format date strings
 */
export const formatDate = (date: string | Date, format: string = 'short'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (format === 'short') {
    return dateObj.toLocaleDateString();
  }
  
  if (format === 'long') {
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  
  if (format === 'time') {
    return dateObj.toLocaleTimeString();
  }
  
  return dateObj.toLocaleString();
};

/** e.g. "Friday, 19 June 2026" */
export const formatTodayLabel = (date: Date = new Date()) => {
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
  const rest = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `${weekday}, ${rest}`;
};

/**
 * Truncate text with ellipsis
 */
export const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate random ID
 */
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Get status color
 */
export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    completed: 'var(--color-success)',
    pending: 'var(--color-warning)',
    failed: 'var(--color-error)',
    processing: 'var(--color-info)',
  };
  
  return statusColors[status.toLowerCase()] || 'var(--color-text-muted)';
};

/**
 * Calculate percentage change
 */
export const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

/** Title-case a name or role: "vivek" → "Vivek", "SUPER_ADMIN" → "Super Admin". */
export function toCapitalizedWords(value?: string | null, fallback = ''): string {
  const text = String(value ?? '').replace(/_/g, ' ').trim();
  if (!text) return fallback;
  return text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
